/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from 'react';

import { EntityLoaders, LoadResults, useLoad } from 'entities';

import { LabeledSelect, LabeledSelectWithNoOptionsProps } from 'components/Blocks';

import { LoadOptions } from 'entities/hooks/load/createHook';

/*
 **  !IMPORTANT!
 **
 **  Due to the nature complexity of the typing, ts can have trouble properly identifying
 **  that the call is correct. However, we've validated that everything here works properly
 **
 **  If any modification is made, make sure to properly test it.
 **
 **  This component basically forwards properties. VSCode correctly infers types
 **  and indicates if the params are wrong (eg multi x value on the select, module-target compatibility)
 */

export type LoadParams<T> = T extends `${infer Module}.${infer Sub}`
  ? Module extends keyof EntityLoaders
    ? Sub extends keyof EntityLoaders[Module]
      ? Pick<
          LoadOptions<EntityLoaders, Module, Sub>,
          'params' | 'onLoad' | 'blockLoad' | 'parser' | 'refreshMilSeconds' | 'refreshMinutes'
        >
      : unknown
    : unknown
  : unknown;

type AllowedSelectProps<Target extends keyof ValidTargets> = LabeledSelectWithNoOptionsProps<
  ValidTargets[Target][number] extends Array<any> ? ValidTargets[Target][number] : any
>;

export type ValidTargets = {
  [K in keyof LoadResults as LoadResults[K] extends any[] ? K : never]: LoadResults[K];
};

type Props<Target extends keyof ValidTargets> = AllowedSelectProps<Target> &
  LoadParams<Target> & {
    // module: Module;
    target: Target;

    filter?: (entity: ValidTargets[Target][number]) => any;
  };

export function EntitySelect<Target extends keyof ValidTargets>({
  target,
  params,
  onLoad,
  blockLoad,
  parser,
  refreshMilSeconds,
  refreshMinutes,
  filter,
  ...selectProps
}: Props<Target>) {
  const [mod, sub] = target.split('.');

  // we know delegation is properly handled
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const entity = useLoad(mod, sub, {
    defaultValue: [],
    params,
    onLoad,
    blockLoad,
    parser,
    refreshMilSeconds,
    refreshMinutes,
  } as LoadParams<Target>);

  const filtered = useMemo(() => {
    if (!filter) return entity.data! as any[];

    return (entity.data! as any[]).filter(filter);
  }, [entity.data, filter]);

  return (
    <LabeledSelect
      isSearchable
      {...selectProps}
      options={filtered}
      loading={entity.errorOrLoading}
    />
  );
}
